import React, { useState } from 'react';
import { AmericanDigitalShortForm } from './AmericanDigitalShortForm';
import { IAmericanDigitalShortFormActions } from './IAmericanDigitalShortFormActions';
import { IAmericanDigitalShortFormData } from './IAmericanDigitalShortFormData';
import { RootStore as MobxRootStore } from '../../services/RootStore';
import { NotificationType } from 'ui-toolkit';
import { selectIsReadyToCreateShortFormPolicy } from './selectIsReadyToCreateShortFormPolicy';
import { PolicyTypeEnum } from '../../classes/PolicyTypeEnum';
import { ePolicyContextActionType } from '../../context/actions/ePolicyContextActionType';
import { usePolicyContext } from '../../context/usePolicyContext';
import { generatePolicyAsync } from 'policyUI/services/DocumentsServiceRequest';
import { useSelectCurrentOrder } from 'selectors/order/selectCurrentOrder';
import { ExternalUrlFactory } from '../../../ui-toolkit/rest/ExternalUrlFactory';
import { ClientUrlFactory } from '../../../ui-toolkit/rest/ClientUrlFactory';
import { eSingleFields } from '../../context/state/eSingleFields';
import { Logger } from '../../../ui-toolkit/logging/LoggerService';

export interface IAmericanDigitalShortFormContainerProps {
  rootStore: MobxRootStore;
}

export const AmericanDigitalShortFormContainer: React.FC<IAmericanDigitalShortFormContainerProps> = ({ rootStore }) => {
  const [, dispatch] = usePolicyContext();
  const [isCreating, setIsCreating] = useState(false);
  const [notification, setNotification] = useState<{ text: string; type: NotificationType }>({
    text: '',
    type: 'success',
  });

  const { data: orderData } = useSelectCurrentOrder();

  const order_id = orderData.transaction.id!;

  const actions: IAmericanDigitalShortFormActions = {
    addLoanNumber: { to: ClientUrlFactory().urlFor.orderStatus(order_id), text: 'Add' },
    addAmountOfInsurance: {
      onClick: () =>
        dispatch({
          type: ePolicyContextActionType.OpenEditSingleFieldAction,
          payload: eSingleFields.loan_amount,
        }),
      text: 'Add',
    },
    addMortgageDate: {
      to: ClientUrlFactory().urlFor.orderStatus(order_id),
      text: 'Add',
    },
    addPolicyNumber: {
      to: ExternalUrlFactory().urlFor.spruceHelpLinkUrl(),
      text: 'Please click here to submit a request for help',
    },
    editMortgageDate: {
      to: ClientUrlFactory().urlFor.orderStatus(order_id),
      text: 'Edit',
    },
    editPremiumAmount: {
      to:
        orderData.fees_version === 'v2'
          ? ClientUrlFactory().urlFor.feesTab(order_id)
          : ClientUrlFactory().urlFor.cdItemsV3(order_id),
      text: 'Edit',
    },
    editBorrowers: { to: ClientUrlFactory().urlFor.orderStatus(order_id), text: 'Edit' },
    editEndorsements: {
      to:
        orderData.fees_version === 'v2'
          ? ClientUrlFactory().urlFor.feesTab(order_id)
          : ClientUrlFactory().urlFor.cdItemsV3(order_id),
      text: 'Edit',
    },
    cancel: () => (window.location.href = ClientUrlFactory().urlFor.title(order_id)),
    createPolicy: () => {
      setIsCreating(true);
      generatePolicyAsync(PolicyTypeEnum.shortForm, order_id)
        .then(() => {
          window.location.href = ClientUrlFactory().urlFor.title(order_id);
        })
        .catch((error) => {
          Logger.error('Unable to generate a short form policy', error);
          setNotification({ text: 'Unable to generate the policy', type: 'error' });
        })
        .finally(() => {
          setIsCreating(false);
        });
    },
  };
  const data: IAmericanDigitalShortFormData = {
    isBusy: isCreating,
    notification,
    file_number: orderData.transaction.file_number,
    policy_number: rootStore.policyScheduleAService.getUnderwriterPolicyId,
    loan_number: orderData.transaction.loan_number,
    property_address: orderData.property,
    amount_of_insurance: orderData.transaction.loan_amount,
    premiums: rootStore.policyScheduleAService.getOrderDetails.premiums,
    name_of_insured: `${rootStore.policyScheduleAService.getOrderDetails.nameOfInsured}${
      rootStore.policyScheduleAService.getOrderDetails.mortgageeClause
        ? `, ${rootStore.policyScheduleAService.getOrderDetails.mortgageeClause}`
        : ''
    }`,
    borrower_full_names: rootStore.policyScheduleAService.getOrderDetails.borrowers,
    endorsements: rootStore.policyScheduleAService.getEndorsements,
    isReadyToCreatePolicy: false,
    mortgage_date: orderData?.orderInformation?.fundingDate,
  };

  data.isReadyToCreatePolicy = selectIsReadyToCreateShortFormPolicy(data);
  const props = { actions, data };
  return <AmericanDigitalShortForm {...props} />;
};
